import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { FunnelButton } from '../FunnelButton';
import styles from './NavigationSecondary.module.scss';

interface INavigationSecondary {
  children: {
    target: string;
    text: string;
  }[];
}

type NavigationSecondaryItemType = {
  bottom: number;
  target: string;
  text: string;
  top: number;
};

export const scrollMarginTop = 160;

export const NavigationSecondary = ({ children }: INavigationSecondary) => {
  const router = useRouter();
  const positionTop = 16;
  const ref = useRef<null | HTMLDivElement>(null);
  const [childrenWithOffsets, setChildrenWithOffsets] = useState<NavigationSecondaryItemType[]>([]);
  const [currentOffset, setCurrentOffset] = useState<number>(0);
  const [fixed, setFixed] = useState(false);
  const [offsetTop, setOffsetTop] = useState<number>(0);

  const getFunnelButtonUrl = useCallback(() => {
    const { pathname } = router;
    if (pathname.includes('/refinancing')) {
      return '/apply/property';
    }
    if (pathname.includes('/buying')) {
      return '/apply/buying';
    }
    return '/start';
  }, [router]);

  const handleMappedValues = useCallback(() => {
    if (document) {
      const { scrollTop } = document.documentElement;
      const boundingClientRect = ref.current ? ref.current.getBoundingClientRect() : { top: 0 };

      setOffsetTop(boundingClientRect.top + scrollTop - positionTop);

      const mappedValues = children.map(({ target, text }) => {
        const targetElement = document.getElementById(target);
        const targetElementBoundingClientRect = targetElement
          ? targetElement.getBoundingClientRect()
          : { bottom: 0, top: 0 };
        const bottom = targetElementBoundingClientRect.bottom + scrollTop || 0;
        const top = targetElementBoundingClientRect.top + scrollTop || 0;
        return {
          bottom,
          target,
          text,
          top,
        };
      });
      setChildrenWithOffsets(mappedValues);
    }
  }, [children, ref]);

  useEffect(() => {
    handleMappedValues();
  }, [handleMappedValues]);

  useEffect(() => {
    window.addEventListener('resize', handleMappedValues);
    return () => window.removeEventListener('resize', handleMappedValues);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setCurrentOffset(offset);
      if (offset > offsetTop) {
        setFixed(true);
      } else {
        setFixed(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [offsetTop]);

  return (
    <>
      <div className={styles.root} ref={ref}>
        <div className={`${styles.control} ${fixed ? styles.fixed : styles.relative}`}>
          <nav className={styles.nav}>
            <ul className={styles.list}>
              {childrenWithOffsets.map(({ bottom, target, text, top }: NavigationSecondaryItemType) => (
                <li className={styles.item} key={text}>
                  <a
                    className={`${styles.link} ${
                      currentOffset + scrollMarginTop > top - positionTop &&
                      currentOffset + scrollMarginTop < bottom - positionTop
                        ? styles.active
                        : styles.inactive
                    }`}
                    href={`#${target}`}
                    onClick={event => {
                      event.preventDefault();
                      document
                        .getElementById(target)
                        ?.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
                    }}
                  >
                    {text}
                  </a>
                </li>
              ))}
              <li className={styles['item-contact']}>
                <button className={styles.contact} type="button">
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path
                      d="M16.6667 1.66602H3.33334C2.41667 1.66602 1.66667 2.41602 1.66667 3.33268V18.3327L5.00001 14.9993H16.6667C17.5833 14.9993 18.3333 14.2493 18.3333 13.3327V3.33268C18.3333 2.41602 17.5833 1.66602 16.6667 1.66602Z"
                      fill="currentColor"
                    />
                  </svg>
                  <span className={styles['contact-text']}>Talk to us</span>
                </button>
              </li>
              <li className={styles['item-apply']}>
                <FunnelButton size="small" url={getFunnelButtonUrl()}>
                  Apply Now
                </FunnelButton>
              </li>
            </ul>
          </nav>
        </div>
      </div>
      <div className={styles.action}>
        <h4 className={styles['action-text']}>Calculate your savings</h4>
        <FunnelButton size="small" url={getFunnelButtonUrl()}>
          Apply Now
        </FunnelButton>
      </div>
    </>
  );
};
